import { useMemo, useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { RepoAction } from '@/components/ui-new/primitives/RepoCard';

export const RIGHT_MAIN_PANEL_MODES = {
  CHANGES: 'changes',
  LOGS: 'logs',
  PREVIEW: 'preview',
} as const;

export type RightMainPanelMode =
  (typeof RIGHT_MAIN_PANEL_MODES)[keyof typeof RIGHT_MAIN_PANEL_MODES];

export type ContextBarPosition =
  | 'top-left'
  | 'top-right'
  | 'middle-left'
  | 'middle-right'
  | 'bottom-left'
  | 'bottom-right';

// Workspace-specific panel state
export type WorkspacePanelState = {
  rightMainPanelMode: RightMainPanelMode | null;
  isLeftMainPanelVisible: boolean;
};

const DEFAULT_WORKSPACE_PANEL_STATE: WorkspacePanelState = {
  rightMainPanelMode: null,
  isLeftMainPanelVisible: true,
};

// Centralized persist keys for type safety
export const PERSIST_KEYS = {
  // Sidebar sections
  workspacesSidebarArchived: 'workspaces-sidebar-archived',
  workspacesSidebarAccordionLayout: 'workspaces-sidebar-accordion-layout',
  workspacesSidebarRaisedHand: 'workspaces-sidebar-raised-hand',
  workspacesSidebarNotRunning: 'workspaces-sidebar-not-running',
  workspacesSidebarRunning: 'workspaces-sidebar-running',
  // Right panel sections
  gitAdvancedSettings: 'git-advanced-settings',
  gitPanelRepositories: 'git-panel-repositories',
  gitPanelProject: 'git-panel-project',
  gitPanelAddRepositories: 'git-panel-add-repositories',
  rightPanelprocesses: 'right-panel-processes',
  rightPanelPreview: 'right-panel-preview',
  // Process panel sections
  processesSection: 'processes-section',
  // Changes panel sections
  changesSection: 'changes-section',
  // Preview panel sections
  devServerSection: 'dev-server-section',
  // Terminal panel section
  terminalSection: 'terminal-section',
  // Notes panel section
  notesSection: 'notes-section',
  // GitHub comments toggle
  showGitHubComments: 'show-github-comments',
  // Panel sizes
  rightMainPanel: 'right-main-panel',
  // Dynamic keys (use helper functions)
  repoCard: (repoId: string) => `repo-card-${repoId}` as const,
} as const;

// Check if screen is wide enough to keep sidebar visible
const isWideScreen = () => window.innerWidth > 2048;

export type PersistKey =
  | typeof PERSIST_KEYS.workspacesSidebarArchived
  | typeof PERSIST_KEYS.workspacesSidebarAccordionLayout
  | typeof PERSIST_KEYS.workspacesSidebarRaisedHand
  | typeof PERSIST_KEYS.workspacesSidebarNotRunning
  | typeof PERSIST_KEYS.workspacesSidebarRunning
  | typeof PERSIST_KEYS.gitAdvancedSettings
  | typeof PERSIST_KEYS.gitPanelRepositories
  | typeof PERSIST_KEYS.gitPanelProject
  | typeof PERSIST_KEYS.gitPanelAddRepositories
  | typeof PERSIST_KEYS.processesSection
  | typeof PERSIST_KEYS.changesSection
  | typeof PERSIST_KEYS.devServerSection
  | typeof PERSIST_KEYS.terminalSection
  | typeof PERSIST_KEYS.notesSection
  | typeof PERSIST_KEYS.showGitHubComments
  | typeof PERSIST_KEYS.rightMainPanel
  | typeof PERSIST_KEYS.rightPanelprocesses
  | typeof PERSIST_KEYS.rightPanelPreview
  | `repo-card-${string}`
  | `diff:${string}`
  | `edit:${string}`
  | `plan:${string}`
  | `tool:${string}`
  | `todo:${string}`
  | `user:${string}`
  | `system:${string}`
  | `error:${string}`
  | `entry:${string}`;

type State = {
  // UI preferences
  repoActions: Record<string, RepoAction>;
  expanded: Record<string, boolean>;
  contextBarPosition: ContextBarPosition;
  paneSizes: Record<string, number | string>;
  collapsedPaths: Record<string, string[]>;

  // Global layout state (applies across all workspaces)
  isLeftSidebarVisible: boolean;
  isRightSidebarVisible: boolean;
  isTerminalVisible: boolean;
  previewRefreshKey: number;

  // Workspace-specific panel state
  workspacePanelStates: Record<string, WorkspacePanelState>;

  // UI preferences actions
  setRepoAction: (repoId: string, action: RepoAction) => void;
  setExpanded: (key: string, value: boolean) => void;
  toggleExpanded: (key: string, defaultValue?: boolean) => void;
  setExpandedAll: (keys: string[], value: boolean) => void;
  setContextBarPosition: (position: ContextBarPosition) => void;
  setPaneSize: (key: string, size: number | string) => void;
  setCollapsedPaths: (key: string, paths: string[]) => void;

  // Layout actions
  toggleLeftSidebar: () => void;
  toggleLeftMainPanel: (workspaceId?: string) => void;
  toggleRightSidebar: () => void;
  toggleTerminal: () => void;
  setTerminalVisible: (value: boolean) => void;
  toggleRightMainPanelMode: (
    mode: RightMainPanelMode,
    workspaceId?: string
  ) => void;
  setRightMainPanelMode: (
    mode: RightMainPanelMode | null,
    workspaceId?: string
  ) => void;
  setLeftSidebarVisible: (value: boolean) => void;
  setLeftMainPanelVisible: (value: boolean, workspaceId?: string) => void;
  triggerPreviewRefresh: () => void;

  // Workspace-specific panel state actions
  getWorkspacePanelState: (workspaceId: string) => WorkspacePanelState;
  setWorkspacePanelState: (
    workspaceId: string,
    state: Partial<WorkspacePanelState>
  ) => void;
};

export const useUiPreferencesStore = create<State>()(
  persist(
    (set, get) => ({
      // UI preferences state
      repoActions: {},
      expanded: {},
      contextBarPosition: 'middle-right',
      paneSizes: {},
      collapsedPaths: {},

      // Global layout state
      isLeftSidebarVisible: true,
      isRightSidebarVisible: true,
      isTerminalVisible: true,
      previewRefreshKey: 0,

      // Workspace-specific panel state
      workspacePanelStates: {},

      // UI preferences actions
      setRepoAction: (repoId, action) =>
        set((s) => ({ repoActions: { ...s.repoActions, [repoId]: action } })),
      setExpanded: (key, value) =>
        set((s) => ({ expanded: { ...s.expanded, [key]: value } })),
      toggleExpanded: (key, defaultValue = true) =>
        set((s) => ({
          expanded: {
            ...s.expanded,
            [key]: !(s.expanded[key] ?? defaultValue),
          },
        })),
      setExpandedAll: (keys, value) =>
        set((s) => ({
          expanded: {
            ...s.expanded,
            ...Object.fromEntries(keys.map((k) => [k, value])),
          },
        })),
      setContextBarPosition: (position) =>
        set({ contextBarPosition: position }),
      setPaneSize: (key, size) =>
        set((s) => ({ paneSizes: { ...s.paneSizes, [key]: size } })),
      setCollapsedPaths: (key, paths) =>
        set((s) => ({ collapsedPaths: { ...s.collapsedPaths, [key]: paths } })),

      // Layout actions
      toggleLeftSidebar: () =>
        set((s) => ({ isLeftSidebarVisible: !s.isLeftSidebarVisible })),

      toggleLeftMainPanel: (workspaceId) => {
        if (!workspaceId) return;
        const state = get();
        const wsState =
          state.workspacePanelStates[workspaceId] ??
          DEFAULT_WORKSPACE_PANEL_STATE;
        if (
          wsState.isLeftMainPanelVisible &&
          wsState.rightMainPanelMode === null
        )
          return;
        set({
          workspacePanelStates: {
            ...state.workspacePanelStates,
            [workspaceId]: {
              ...wsState,
              isLeftMainPanelVisible: !wsState.isLeftMainPanelVisible,
            },
          },
        });
      },

      toggleRightSidebar: () =>
        set((s) => ({ isRightSidebarVisible: !s.isRightSidebarVisible })),

      toggleTerminal: () =>
        set((s) => ({ isTerminalVisible: !s.isTerminalVisible })),

      setTerminalVisible: (value) => set({ isTerminalVisible: value }),

      toggleRightMainPanelMode: (mode, workspaceId) => {
        if (!workspaceId) return;
        const state = get();
        const wsState =
          state.workspacePanelStates[workspaceId] ??
          DEFAULT_WORKSPACE_PANEL_STATE;
        const isCurrentlyActive = wsState.rightMainPanelMode === mode;

        set({
          workspacePanelStates: {
            ...state.workspacePanelStates,
            [workspaceId]: {
              ...wsState,
              rightMainPanelMode: isCurrentlyActive ? null : mode,
            },
          },
          isLeftSidebarVisible: isCurrentlyActive
            ? true
            : isWideScreen()
              ? state.isLeftSidebarVisible
              : false,
        });
      },

      setRightMainPanelMode: (mode, workspaceId) => {
        if (!workspaceId) return;
        const state = get();
        const wsState =
          state.workspacePanelStates[workspaceId] ??
          DEFAULT_WORKSPACE_PANEL_STATE;
        set({
          workspacePanelStates: {
            ...state.workspacePanelStates,
            [workspaceId]: {
              ...wsState,
              rightMainPanelMode: mode,
            },
          },
          ...(mode !== null && {
            isLeftSidebarVisible: isWideScreen()
              ? state.isLeftSidebarVisible
              : false,
          }),
        });
      },

      setLeftSidebarVisible: (value) => set({ isLeftSidebarVisible: value }),

      setLeftMainPanelVisible: (value, workspaceId) => {
        if (!workspaceId) return;
        const state = get();
        const wsState =
          state.workspacePanelStates[workspaceId] ??
          DEFAULT_WORKSPACE_PANEL_STATE;
        set({
          workspacePanelStates: {
            ...state.workspacePanelStates,
            [workspaceId]: {
              ...wsState,
              isLeftMainPanelVisible: value,
            },
          },
        });
      },

      triggerPreviewRefresh: () =>
        set((s) => ({ previewRefreshKey: s.previewRefreshKey + 1 })),

      // Workspace-specific panel state actions
      getWorkspacePanelState: (workspaceId) => {
        const state = get();
        return (
          state.workspacePanelStates[workspaceId] ??
          DEFAULT_WORKSPACE_PANEL_STATE
        );
      },

      setWorkspacePanelState: (workspaceId, panelState) => {
        const state = get();
        const currentWsState =
          state.workspacePanelStates[workspaceId] ??
          DEFAULT_WORKSPACE_PANEL_STATE;
        set({
          workspacePanelStates: {
            ...state.workspacePanelStates,
            [workspaceId]: {
              ...currentWsState,
              ...panelState,
            },
          },
        });
      },
    }),
    {
      name: 'ui-preferences',
      partialize: (state) => ({
        // UI preferences (all persisted)
        repoActions: state.repoActions,
        expanded: state.expanded,
        contextBarPosition: state.contextBarPosition,
        paneSizes: state.paneSizes,
        collapsedPaths: state.collapsedPaths,
        // Global layout (persist sidebar visibility)
        isLeftSidebarVisible: state.isLeftSidebarVisible,
        isRightSidebarVisible: state.isRightSidebarVisible,
        isTerminalVisible: state.isTerminalVisible,
        // Workspace-specific panel state (persisted)
        workspacePanelStates: state.workspacePanelStates,
      }),
    }
  )
);

// Hook for repo action preference
export function useRepoAction(
  repoId: string,
  defaultAction: RepoAction = 'pull-request'
): [RepoAction, (action: RepoAction) => void] {
  const action = useUiPreferencesStore(
    (s) => s.repoActions[repoId] ?? defaultAction
  );
  const setAction = useUiPreferencesStore((s) => s.setRepoAction);
  return [action, (a) => setAction(repoId, a)];
}

// Hook for persisted expanded state
export function usePersistedExpanded(
  key: PersistKey,
  defaultValue = true
): [boolean, (value?: boolean) => void] {
  const expanded = useUiPreferencesStore(
    (s) => s.expanded[key] ?? defaultValue
  );
  const setExpanded = useUiPreferencesStore((s) => s.setExpanded);
  const toggleExpanded = useUiPreferencesStore((s) => s.toggleExpanded);

  const set = (value?: boolean) => {
    if (typeof value === 'boolean') setExpanded(key, value);
    else toggleExpanded(key, defaultValue);
  };

  return [expanded, set];
}

// Hook for context bar position
export function useContextBarPosition(): [
  ContextBarPosition,
  (position: ContextBarPosition) => void,
] {
  const position = useUiPreferencesStore((s) => s.contextBarPosition);
  const setPosition = useUiPreferencesStore((s) => s.setContextBarPosition);
  return [position, setPosition];
}

// Hook for pane size preference
export function usePaneSize(
  key: PersistKey,
  defaultSize: number | string
): [number | string, (size: number | string) => void] {
  const size = useUiPreferencesStore((s) => s.paneSizes[key] ?? defaultSize);
  const setSize = useUiPreferencesStore((s) => s.setPaneSize);
  return [size, (s) => setSize(key, s)];
}

// Hook for bulk expanded state operations
export function useExpandedAll() {
  const expanded = useUiPreferencesStore((s) => s.expanded);
  const setExpanded = useUiPreferencesStore((s) => s.setExpanded);
  const setExpandedAll = useUiPreferencesStore((s) => s.setExpandedAll);
  return { expanded, setExpanded, setExpandedAll };
}

// Hook for persisted file tree collapsed paths (per workspace)
export function usePersistedCollapsedPaths(
  workspaceId: string | undefined
): [Set<string>, (paths: Set<string>) => void] {
  const key = workspaceId ? `file-tree:${workspaceId}` : '';
  const paths = useUiPreferencesStore((s) => s.collapsedPaths[key] ?? []);
  const setPaths = useUiPreferencesStore((s) => s.setCollapsedPaths);

  const pathSet = useMemo(() => new Set(paths), [paths]);
  const setPathSet = useCallback(
    (newPaths: Set<string>) => {
      if (key) setPaths(key, [...newPaths]);
    },
    [key, setPaths]
  );

  return [pathSet, setPathSet];
}

// Hook for workspace-specific panel state
export function useWorkspacePanelState(workspaceId: string | undefined) {
  // Get workspace-specific state (falls back to defaults when no workspaceId)
  const workspacePanelStates = useUiPreferencesStore(
    (s) => s.workspacePanelStates
  );
  const wsState = workspaceId
    ? (workspacePanelStates[workspaceId] ?? DEFAULT_WORKSPACE_PANEL_STATE)
    : DEFAULT_WORKSPACE_PANEL_STATE;

  // Global state (sidebars are global)
  const isLeftSidebarVisible = useUiPreferencesStore(
    (s) => s.isLeftSidebarVisible
  );
  const isRightSidebarVisible = useUiPreferencesStore(
    (s) => s.isRightSidebarVisible
  );
  const isTerminalVisible = useUiPreferencesStore((s) => s.isTerminalVisible);

  // Actions from store
  const toggleRightMainPanelMode = useUiPreferencesStore(
    (s) => s.toggleRightMainPanelMode
  );
  const setRightMainPanelMode = useUiPreferencesStore(
    (s) => s.setRightMainPanelMode
  );
  const setLeftMainPanelVisible = useUiPreferencesStore(
    (s) => s.setLeftMainPanelVisible
  );
  const setLeftSidebarVisible = useUiPreferencesStore(
    (s) => s.setLeftSidebarVisible
  );

  // Memoized callbacks that include workspaceId
  const toggleRightMainPanelModeForWorkspace = useCallback(
    (mode: RightMainPanelMode) => toggleRightMainPanelMode(mode, workspaceId),
    [toggleRightMainPanelMode, workspaceId]
  );

  const setRightMainPanelModeForWorkspace = useCallback(
    (mode: RightMainPanelMode | null) =>
      setRightMainPanelMode(mode, workspaceId),
    [setRightMainPanelMode, workspaceId]
  );

  const setLeftMainPanelVisibleForWorkspace = useCallback(
    (value: boolean) => setLeftMainPanelVisible(value, workspaceId),
    [setLeftMainPanelVisible, workspaceId]
  );

  return {
    // Workspace-specific state
    rightMainPanelMode: wsState.rightMainPanelMode,
    isLeftMainPanelVisible: wsState.isLeftMainPanelVisible,

    // Global state (sidebars and terminal)
    isLeftSidebarVisible,
    isRightSidebarVisible,
    isTerminalVisible,

    // Workspace-specific actions
    toggleRightMainPanelMode: toggleRightMainPanelModeForWorkspace,
    setRightMainPanelMode: setRightMainPanelModeForWorkspace,
    setLeftMainPanelVisible: setLeftMainPanelVisibleForWorkspace,

    // Global actions
    setLeftSidebarVisible,
  };
}
